import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow,
    Box,
    Stack,
    IconButton,
    TextField,
    Menu,
    MenuItem
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import EditIcon from '../../Images/edit.png'
import DeleteIcon from '../../Images/delete.png'
import { Status } from "../../HelperFunctions/HelperFunctions";
import { SETUP_FILTER_OPTIONS } from "../../Enums/Enum";
import { Loader } from "../../Components/Loader";
import { payrollSetupStyles } from "../../styles/payrollSetup.styles";

const {
    tableContainer: TABLE_CONTAINER_SX,
    bodyScroll: BODY_SCROLL_SX,
    headerFilterCell: HEADER_FILTER_CELL_SX,
    bodyCellBase: BODY_CELL_BASE_SX,
} = payrollSetupStyles;

// --- Subcomponents ---
const HeaderCell = memo(({ children, sx, ...props }) => (
    <TableCell
        component="th"
        scope="col"
        sx={{
            ...payrollSetupStyles.headerCellBase,
            ...sx
        }}
        {...props}
    >
        {children}
    </TableCell>
));

const ActionButtons = memo(({ rowData, onEdit, onDelete }) => (
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={payrollSetupStyles.actionButtonsStack}>
        <span title="Edit">
            <IconButton
                size="small"
                onClick={() => onEdit(rowData)}
                sx={payrollSetupStyles.actionIconButton}
                aria-label={`Edit payroll setup for ${rowData?.itemName || 'item'}`}
            >
                <img src={EditIcon} style={{ cursor: "pointer" }} alt="edit" width={18} height={18} />
            </IconButton>
        </span>
        <span title="Delete">
            <IconButton
                size="small"
                onClick={() => onDelete(rowData)}
                sx={payrollSetupStyles.actionIconButton}
                aria-label={`Delete payroll setup for ${rowData?.itemName || 'item'}`}
            >
                <img style={{ cursor: "pointer" }} src={DeleteIcon} alt="delete" width={18} height={18} />
            </IconButton>
        </span>
    </Stack>
));

const EnhancedValueCell = memo(({ row, keyName }) => {
    // Handle status column
    if (keyName === "status") {
        return <span title={row[keyName]}><Status status={row[keyName]} /></span>;
    }

    // Handle regular text columns
    if (!["department", "jobtitle", "account", "liabilityAccount"].includes(keyName)) {
        const val = row[keyName] ?? "";
        return <span title={val}>{val}</span>;
    }

    const cellValue = row[keyName];

    // Element is already pre-configured (e.g. RenderMultiple component)
    if (React.isValidElement(cellValue)) {
        return cellValue;
    }

    // Simple string value fallback
    return <span title={cellValue || ""}>{cellValue || ""}</span>;
});

const SetupTable = ({ rows, columns, onFilterChange, filters, loading, onEdit, onDelete }) => {

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [currentMenuDataKey, setCurrentMenuDataKey] = useState(null);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);
    const [actionsColWidth, setActionsColWidth] = useState(0);
    const bodyScrollRef = useRef(null);
    const actionsSizerRef = useRef(null);

    const getColumnWidth = (width) => {
        const parsed = Number(width);
        return Number.isFinite(parsed) ? parsed : 140;
    };

    const tableMinWidth = columns.reduce((sum, col) => sum + getColumnWidth(col.width), 0) + actionsColWidth;
    const actionsColSx = actionsColWidth
        ? { width: actionsColWidth, minWidth: actionsColWidth, maxWidth: actionsColWidth }
        : {};

    // --- Effects ---
    useEffect(() => {
        const updateScrollbarState = () => {
            const el = bodyScrollRef.current;
            if (!el) return;
            const currentScrollbarWidth = el.offsetWidth - el.clientWidth;
            const isOverflowing = el.scrollHeight > el.clientHeight + 1;
            setScrollbarWidth(currentScrollbarWidth);
            setHasVerticalScrollbar(isOverflowing && currentScrollbarWidth > 0);
        };

        updateScrollbarState();
        const rafId = requestAnimationFrame(updateScrollbarState);
        window.addEventListener("resize", updateScrollbarState);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", updateScrollbarState);
        };
    }, [rows, loading]);

    useEffect(() => {
        const updateActionsWidth = () => {
            const el = actionsSizerRef.current;
            if (!el) return;
            const measured = Math.ceil(el.getBoundingClientRect().width);
            if (measured > 0) {
                setActionsColWidth(measured);
            }
        };

        updateActionsWidth();
        const rafId = requestAnimationFrame(updateActionsWidth);
        window.addEventListener("resize", updateActionsWidth);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", updateActionsWidth);
        };
    }, []);

    // --- Handlers ---
    const handleMenuOpen = useCallback((event, dataKey) => {
        setMenuAnchor(event.currentTarget);
        setCurrentMenuDataKey(dataKey);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
        setCurrentMenuDataKey(null);
    }, []);

    const handleFilterOptionChange = useCallback((option) => {
        const currentFilter = filters[currentMenuDataKey] || {};
        const newFilter = { ...currentFilter, option: option.value };
        onFilterChange({ ...filters, [currentMenuDataKey]: newFilter });
        handleMenuClose();
    }, [filters, currentMenuDataKey, onFilterChange, handleMenuClose]);

    const handleFilterTextChange = useCallback((dataKey, value) => {
        const currentFilter = filters[dataKey] || {};
        const newFilter = { ...currentFilter, term: value };
        if (dataKey === "status") {
            onFilterChange({ ...filters, status: newFilter });
        } else {
            onFilterChange({ ...filters, [dataKey]: newFilter });
        }
    }, [filters, onFilterChange]);

    return (
        <>
            <Box
                ref={actionsSizerRef}
                sx={payrollSetupStyles.actionsSizerBox}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton size="small" sx={payrollSetupStyles.actionIconButton}>
                        <img src={EditIcon} alt="" width={18} height={18} />
                    </IconButton>
                    <IconButton size="small" sx={payrollSetupStyles.actionIconButton}>
                        <img src={DeleteIcon} alt="" width={18} height={18} />
                    </IconButton>
                </Stack>
            </Box>

            <TableContainer
                sx={TABLE_CONTAINER_SX}
            >
                <Stack sx={{ minWidth: tableMinWidth, height: "100%" }}>
                    <Box sx={{ pr: hasVerticalScrollbar ? `${scrollbarWidth + 1}px` : 0, boxSizing: "border-box" }}>
                        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                            <TableHead sx={{ backgroundColor: "#ffffff" }}>
                                <TableRow>
                                    {columns.map((col, idx) => {
                                        const width = getColumnWidth(col.width);
                                        return (
                                            <HeaderCell 
                                                key={idx} 
                                                sx={{ 
                                                    width, 
                                                    minWidth: width, 
                                                    maxWidth: width, 
                                                    textAlign: col.dataKey === 'status' ? 'center' : 'left' 
                                                }}>
                                                <span title={col.name}>{col.name}</span>
                                            </HeaderCell>
                                        );
                                    })}
                                    <HeaderCell
                                        sx={{
                                            borderLeft: "1px solid #f2f2f2",
                                            textAlign: "center",
                                            ...actionsColSx
                                        }}
                                    >
                                        <span title="Actions">Actions</span>
                                    </HeaderCell>
                                </TableRow>

                                <TableRow sx={{ backgroundColor: "#ffffff" }}>
                                    {columns.map((col) => {
                                        const width = getColumnWidth(col.width);
                                        return (
                                            <TableCell
                                                key={`${col.dataKey}-filter`}
                                                sx={{
                                                    width,
                                                    minWidth: width,
                                                    maxWidth: width,
                                                    ...HEADER_FILTER_CELL_SX
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={0}>
                                                    <TextField
                                                        variant="outlined"
                                                        fullWidth
                                                        placeholder="Filter"
                                                        value={filters?.[col.dataKey]?.term || ""}
                                                        onChange={(e) => handleFilterTextChange(col.dataKey, e.target.value)}
                                                        sx={payrollSetupStyles.filterTextField}
                                                    />
                                                    <span title="Filter">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleMenuOpen(e, col.dataKey)}
                                                            sx={payrollSetupStyles.filterIconButton}
                                                        >
                                                            <FilterAltIcon sx={payrollSetupStyles.filterIcon} />
                                                        </IconButton>
                                                    </span>
                                                </Stack>
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell
                                        sx={{
                                            ...actionsColSx,
                                            ...HEADER_FILTER_CELL_SX
                                        }}
                                    />
                                </TableRow>
                            </TableHead>
                        </Table>
                    </Box>

                    <TableContainer
                        ref={bodyScrollRef}
                        sx={BODY_SCROLL_SX}
                    >
                        <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6, position: "relative" }}>
                                            <Loader loading={loading} />
                                        </TableCell>
                                    </TableRow>
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} align="center" sx={payrollSetupStyles.emptyStateCell}>
                                            No Data Found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row, rowIndex) => (
                                        <TableRow key={rowIndex} hover sx={payrollSetupStyles.tableRowHover}>
                                            {columns.map((col, idx) => {
                                                const width = getColumnWidth(col.width);
                                                return (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            width,
                                                            minWidth: width,
                                                            maxWidth: width,
                                                            ...BODY_CELL_BASE_SX,
                                                            padding: "5.1px",
                                                            paddingLeft: (col.dataKey === "itemType" || col.dataKey === "itemName") ? "6.1px" : "5.1px",
                                                            textAlign: col.dataKey === "status" ? "center" : "left"
                                                        }}
                                                    >
                                                        <EnhancedValueCell row={row} keyName={col.dataKey} />
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell
                                                sx={{
                                                    ...actionsColSx,
                                                    borderBottom: "1px solid #f2f2f2",
                                                    borderRight: "1px solid #f2f2f2",
                                                    padding: "0px 5px",
                                                    textAlign: "center",
                                                    verticalAlign: "middle"
                                                }}
                                            >
                                                <ActionButtons rowData={row} onEdit={onEdit} onDelete={onDelete} />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: payrollSetupStyles.filterMenuPaper
                }}
            >
                {SETUP_FILTER_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => handleFilterOptionChange(option)}
                        selected={option.value === (filters?.[currentMenuDataKey]?.option || "contains")}
                        sx={payrollSetupStyles.filterMenuItem}
                        title={option.label}
                    >
                        <span title={option.label}>{option.label}</span>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default SetupTable;
